import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Form, FormField, FormItem } from '@/components/ui/form/form';
import { ViewHeader } from '@/components/view-layout';
import { useAutoSave } from '@/hooks/use-auto-save';
import formatLastModified from '@/lib/format-last-modified';

import { useEditIngredientDetails } from '../api/ingredients-mutations';
import { IngredientWithDetails } from '../types/ingredient-types';

const titleFormSchema = z.object({
  title: z.string().min(1, 'Name is required'),
});

type TitleForm = z.infer<typeof titleFormSchema>;

interface IngredientEditHeaderPresentationProps {
  ingredient: IngredientWithDetails;
  onSubmit: (values: TitleForm) => Promise<void>;
  isEditable: boolean;
}

function IngredientEditHeaderPresentation({
  ingredient,
  isEditable,
  onSubmit,
}: IngredientEditHeaderPresentationProps) {
  const form = useForm<TitleForm>({
    resolver: zodResolver(titleFormSchema),
    defaultValues: {
      title: ingredient.title,
    },
  });

  const lastModified = Math.max(
    new Date(ingredient.updated_at).getTime(),
    new Date(
      ingredient.latest_ingredient_allergen[0]?.created_at ?? 0,
    ).getTime(),
    new Date(
      ingredient.latest_ingredient_nutrient[0]?.created_at ?? 0,
    ).getTime(),
  );

  const lastModifiedString = formatLastModified(new Date(lastModified));

  useAutoSave(form, onSubmit);

  return (
    <ViewHeader
      titleComponent={
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-8 w-full"
          >
            {/* Ingredient Name */}
            <FormField
              control={form.control}
              name="title"
              disabled={!isEditable}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <input
                    type="text"
                    placeholder="Ingredient Name"
                    className="bg-transparent border-b-2 border-gray-300 focus:border-primary focus:outline-none text-2xl font-semibold w-full"
                    aria-label="Ingredient Name"
                    {...field}
                  />
                </FormItem>
              )}
            />
          </form>
        </Form>
      }
      titleClassName="flex-1"
      actionsClassName="flex-1"
    >
      <div className="flex justify-end items-center gap-4 w-full">
        <span>Last edit: {lastModifiedString}</span>
      </div>
    </ViewHeader>
  );
}

interface IngredientEditHeaderProps {
  ingredient: IngredientWithDetails;
  isEditable: boolean;
}

export function IngredientEditHeader({
  ingredient,
  isEditable,
}: IngredientEditHeaderProps) {
  const editIngredientDetails = useEditIngredientDetails();

  const onSubmit = useCallback(
    async (values: TitleForm) => {
      await editIngredientDetails.mutateAsync({
        id: ingredient.id,
        title: values.title,
      });
    },
    [editIngredientDetails, ingredient.id],
  );

  return (
    <IngredientEditHeaderPresentation
      ingredient={ingredient}
      onSubmit={onSubmit}
      isEditable={isEditable}
      key={ingredient.id}
    />
  );
}
